import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Map, { Marker, Popup, NavigationControl, AttributionControl, useMap } from 'react-map-gl/maplibre';
import { Text, Anchor, useComputedColorScheme } from '@mantine/core';
import useSWR from 'swr';
import { AxiosError } from 'axios';
import 'maplibre-gl/dist/maplibre-gl.css';
import './DeviceMap.css';
import type { Location } from '../data/Location';
import { Device, DeviceLatestPing, listDeviceFetcher } from '../data/Device';
import { appConfig } from '../data/AppConfig';

const LIVE_WINDOW_MS = 5 * 60 * 1000;

const describePlaying = (ping: DeviceLatestPing): string => {
    if (ping.isCommercial) return 'Commercial break';
    if (ping.isSport && ping.currentSport) return ping.currentSport;
    if (ping.isSport) return 'Sport';
    return 'Idle';
};

const MAPTILER_KEY = appConfig.REACT_APP_MAPTILER_KEY;
const MAP_STYLE_LIGHT = MAPTILER_KEY
    ? `https://api.maptiler.com/maps/streets-v2-light/style.json?key=${MAPTILER_KEY}`
    : 'https://tiles.openfreemap.org/styles/liberty';
const MAP_STYLE_DARK = MAPTILER_KEY
    ? `https://api.maptiler.com/maps/streets-v2-dark/style.json?key=${MAPTILER_KEY}`
    : 'https://tiles.openfreemap.org/styles/dark';

type Props = {
    locations: Location[];
    height?: number;
};

type ViewState = {
    longitude: number;
    latitude: number;
    zoom: number;
};

type Bounds = [[number, number], [number, number]];

type PopupAnchor = 'bottom' | 'top' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

const EDGE_THRESHOLD = 90; // px from edge before flipping anchor

function getAnchor(map: maplibregl.Map, lng: number, lat: number): PopupAnchor {
    const point = map.project([lng, lat]);
    const { clientWidth: w, clientHeight: h } = map.getContainer();

    const nearTop = point.y < EDGE_THRESHOLD;
    const nearLeft = point.x < EDGE_THRESHOLD;
    const nearRight = point.x > w - EDGE_THRESHOLD;

    if (nearTop && nearLeft) return 'top-left';
    if (nearTop && nearRight) return 'top-right';
    if (nearTop) return 'top';
    if (nearLeft) return 'left';
    if (nearRight) return 'right';
    return 'bottom';
}

function getInitialViewState(mapped: Location[]): { viewState?: ViewState; bounds?: Bounds } {
    if (mapped.length === 0) {
        return { viewState: { longitude: -98, latitude: 39, zoom: 3.5 } };
    }
    if (mapped.length === 1) {
        return { viewState: { longitude: mapped[0].longitude!, latitude: mapped[0].latitude!, zoom: 11 } };
    }
    const lngs = mapped.map(l => l.longitude!);
    const lats = mapped.map(l => l.latitude!);
    return {
        bounds: [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)],
        ],
    };
}

type LocationPopupsProps = {
    locations: Location[];
    devices: Device[];
    selectedId: string | null;
    onSelect: (id: string | null) => void;
};

const LocationPopups: React.FC<LocationPopupsProps> = ({ locations, devices, selectedId, onSelect }) => {
    const { current: map } = useMap();
    const navigate = useNavigate();
    const [, setTick] = useState(0);

    const devicesByLocation = React.useMemo(() => {
        const map: Record<string, Device[]> = {};
        for (const d of devices) {
            if (!d.locationId) continue;
            (map[d.locationId] ??= []).push(d);
        }
        return map;
    }, [devices]);

    useEffect(() => {
        if (!map) return;
        const refresh = () => setTick(n => n + 1);
        map.on('move', refresh);
        return () => { map.off('move', refresh); };
    }, [map]);

    return (
        <>
            {locations.map(loc => {
                const isSelected = selectedId === loc.id;
                return (
                    <Marker
                        key={loc.id}
                        longitude={loc.longitude!}
                        latitude={loc.latitude!}
                        anchor="center"
                    >
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelect(isSelected ? null : loc.id);
                            }}
                            style={{
                                width: isSelected ? 18 : 14,
                                height: isSelected ? 18 : 14,
                                borderRadius: '50%',
                                background: '#6D6DDC',
                                border: '2.5px solid white',
                                boxShadow: isSelected
                                    ? '0 2px 8px rgba(109,109,220,0.7)'
                                    : '0 1px 4px rgba(0,0,0,0.35)',
                                cursor: 'pointer',
                                transition: 'width 120ms ease, height 120ms ease, box-shadow 120ms ease',
                            }}
                        />
                    </Marker>
                );
            })}

            {locations.map(loc => {
                if (selectedId !== loc.id) return null;
                const anchor = map ? getAnchor(map.getMap(), loc.longitude!, loc.latitude!) : 'bottom';
                return (
                    <Popup
                        key={loc.id}
                        longitude={loc.longitude!}
                        latitude={loc.latitude!}
                        anchor={anchor}
                        closeButton={false}
                        closeOnClick={false}
                        offset={14}
                        className="watchly-popup"
                    >
                        <Anchor
                            onClick={() => navigate(`/location/${loc.id}`)}
                            fw={700}
                            size="sm"
                            style={{ color: '#fff', display: 'block', marginBottom: 2, cursor: 'pointer' }}
                        >
                            {loc.name}
                        </Anchor>
                        <Text size="xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                            {loc.deviceCount} {loc.deviceCount === 1 ? 'device' : 'devices'}
                        </Text>
                        {(() => {
                            const locDevices = devicesByLocation[loc.id] ?? [];
                            if (locDevices.length === 0) return null;
                            return (
                                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {locDevices.map(d => {
                                        const ping = d.latestPing;
                                        const isLive =
                                            ping != null &&
                                            Date.now() - new Date(ping.createdAt).getTime() < LIVE_WINDOW_MS;
                                        return (
                                            <div
                                                key={d.id}
                                                onClick={() => navigate(`/device/${d.id}`)}
                                                className="watchly-popup-device"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    cursor: 'pointer',
                                                    padding: '2px 4px',
                                                    borderRadius: 3,
                                                }}
                                            >
                                                {isLive && (
                                                    <span className="live-dot" aria-label="live" />
                                                )}
                                                <Text
                                                    size="xs"
                                                    style={{ color: '#fff', fontWeight: 500 }}
                                                >
                                                    {d.name}
                                                </Text>
                                                {isLive && ping && (
                                                    <Text
                                                        size="xs"
                                                        style={{ color: 'rgba(255,255,255,0.55)' }}
                                                    >
                                                        — {describePlaying(ping)}
                                                    </Text>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                    </Popup>
                );
            })}
        </>
    );
};

const DeviceMap: React.FC<Props> = ({ locations, height = 400 }) => {
    const scheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
    const mapped = locations.filter(l => l.latitude != null && l.longitude != null);
    const { viewState, bounds } = getInitialViewState(mapped);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    // Self-fetch devices so popups can show "currently playing". Refresh keeps
    // the live indicator current without depending on the parent component.
    const { data: devices } = useSWR<Device[], AxiosError>(
        'api/devices',
        listDeviceFetcher,
        { refreshInterval: 30_000 },
    );

    const initialViewState = viewState
        ? viewState
        : { bounds: bounds!, fitBoundsOptions: { padding: 60 } };

    return (
        <Map
            initialViewState={initialViewState}
            style={{ width: '100%', height, borderRadius: 8 }}
            mapStyle={scheme === 'dark' ? MAP_STYLE_DARK : MAP_STYLE_LIGHT}
            attributionControl={false}
            reuseMaps
            onClick={() => setSelectedId(null)}
        >
            <NavigationControl position="top-right" />
            <AttributionControl compact />

            <LocationPopups
                locations={mapped}
                devices={devices ?? []}
                selectedId={selectedId}
                onSelect={setSelectedId}
            />
        </Map>
    );
};

export default DeviceMap;
